// V2-5c: pure selectors over the archive's `game` key
// (`utils/watch_game.py::compute_watch_game`, merged onto
// `3a/watch-archive.json` by `utils/watch_projection.py::KEY_GAME` — see
// that module's own integration point, one line, cited from
// `watch_game.py`'s module docstring). No React here, on purpose — the
// design's own `boards()`/`memberVals()` (`D2D Watch v2.dc.html`) are the
// shape this file's exports replace, over REAL data instead of the
// prototype's invented `LB`/`HOF`/`BADGES` constants.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 A MISSING `game` IS A REAL, DISTINCT STATE — NEVER AN EMPTY BOARD
// ═══════════════════════════════════════════════════════════════════════════
//
// `game` is a key added to the SAME archive blob `game.GAME_VERSION`
// versions independently of `WATCH_BLOB_VERSION` (see that constant's own
// comment) — so an archive published before V2-5a merged has `titles` but
// no `game` at all. `gameOf` is what tells the two apart: `null` means
// "not published yet," never "nobody has watched anything." The caller
// (`WatchLeaderboards.jsx`) renders one plain line for the first case and
// never renders a board with real-looking zeros for it — CLAUDE.md's own
// "fail loud, never degrade silently," applied to a derived feature rather
// than a store.
//
// ═══════════════════════════════════════════════════════════════════════════
// ✅ BADGES HAVE A SELECTOR AS OF V2-5f. THE CONTENT BOARDS HAVE ONE TOO, AS
//    OF V2-5g.
// ═══════════════════════════════════════════════════════════════════════════
//
// This paragraph used to say badges had "no selector here, deliberately,"
// right alongside the content boards — true when V2-5c wrote it, and stale
// the moment V2-5d published `game["badges"]` / `game.members[id].badges`.
// `utils/watch_game.py`'s own module docstring corrected itself the
// identical way ("Badges are IN scope as of V2-5d — this note used to say
// they were not"); this file's comment just hadn't caught up yet.
// `badgeCards`/`memberBadges`, near the bottom of this file, are that
// selector.
//
// The content boards were a DIFFERENT gap for two more slices: `game`
// carried no per-title content-board key at all until V2-5e published
// `game["content_boards"]` (`utils/watch_content_boards.py::
// compute_content_boards`, merged onto `compute_watch_game`'s own return
// value). `contentBoardTabs`/`contentBoardRows`/`contentBoardNote`/
// `contentBoardEmptyText`/`contentBoardsPublished`, near the bottom of this
// file, are THAT selector — gotcha 21's own rule applied here too: this
// file draws exactly the six boards, in exactly the order and rank the bot
// already computed, and never re-filters, re-sorts, or re-derives a single
// row of one. The design's own "What the group rates" section (`docs/
// design-briefs/watch/v2/D2D Watch v2.dc.html`'s `boards()`'s `cbTabs`/
// `cb`) is what these selectors and `WatchLeaderboards.jsx`'s own section
// now draw from real data, over the design's invented sample rows.

/** `shared.game`, or `null` for anything that is not a genuine object —
 * covers both "the key is absent" (an archive published before V2-5a) and
 * a defensively-rejected malformed value. Never throws. */
export function gameOf(shared) {
  const game = shared?.game
  return game && typeof game === 'object' && !Array.isArray(game) ? game : null
}

// ═══════════════════════════════════════════════════════════════════════════
// Vocabulary — mirrors utils/watch_game.py's own constants (the wire
// strings, `game.boards[period][metric]`), duplicated rather than shared
// across the Python/JS boundary the same way CLAUDE.md gotcha 21 already
// documents for `crew_average_rating`/`crewAverageRating`: two languages,
// one contract, pinned against each other by tests/test_watch_game_
// fixture.py's shared fixture rather than assumed to agree.
// ═══════════════════════════════════════════════════════════════════════════

export const METRIC_HOURS = 'hours'
export const METRIC_FINISHED = 'finished'
export const METRIC_EPISODES = 'episodes'
export const METRIC_REVIEWS = 'reviews'
export const METRIC_STREAK = 'streak'

/** Display order — HANDOFF.md §3's own words, "Metric (Hours, Finished,
 * Episodes, Reviews, Streak)". Deliberately NOT `utils/watch_game.py::
 * METRICS`' tuple order (finished first): that tuple is an implementation
 * detail of how the bot accumulates a row, this is what the tabs render,
 * and the brief's own default ("Hours · Season") only makes sense if
 * Hours renders first. */
export const BOARD_METRICS = Object.freeze([
  METRIC_HOURS,
  METRIC_FINISHED,
  METRIC_EPISODES,
  METRIC_REVIEWS,
  METRIC_STREAK,
])

export const PERIOD_SEASON = 'season'
export const PERIOD_YEAR = 'year'
export const PERIOD_ALL_TIME = 'all_time'

export const PERIODS = Object.freeze([PERIOD_SEASON, PERIOD_YEAR, PERIOD_ALL_TIME])

/** Brief §0: "The default is Hours · Season." */
export const DEFAULT_METRIC = METRIC_HOURS
export const DEFAULT_PERIOD = PERIOD_SEASON

const METRIC_LABEL = Object.freeze({
  [METRIC_HOURS]: 'Hours',
  [METRIC_FINISHED]: 'Finished',
  [METRIC_EPISODES]: 'Episodes',
  [METRIC_REVIEWS]: 'Reviews',
  [METRIC_STREAK]: 'Streak',
})

const PERIOD_LABEL = Object.freeze({
  // The prototype's own literal copy (`D2D Watch v2.dc.html`'s `lbPeriods`)
  // — RECONCILIATION.md's acceptance rule, "the design's copy verbatim."
  [PERIOD_SEASON]: 'This season',
  [PERIOD_YEAR]: 'This year',
  [PERIOD_ALL_TIME]: 'All time',
})

/**
 * One line per metric, said from the real definition — spec §4
 * (`docs/superpowers/specs/2026-09-22-watch-v2-design.md`) and
 * `utils/watch_game.py`'s module docstring, never the prototype's own
 * invented `LB[k].note` strings (HANDOFF.md line 11: "all data is
 * invented"). The prototype's own streak note reads "Longest run of DAYS"
 * — wrong for this store, and brief §0 says so explicitly: "The streak is
 * in WEEKS ... Say so in its note."
 */
// Manager review: the SAME words as Discord's `/watch leaderboard`
// (`cogs/watch/leaderboard.py::_BOARD_NOTES`), because a board must explain
// itself identically on both surfaces. Keep the two in step.
const METRIC_NOTE = Object.freeze({
  [METRIC_HOURS]:
    'Episodes times each title’s runtime; a movie counts its runtime. A title you’re still ' +
    'watching counts toward All time only, and a title with no runtime on file counts zero hours.',
  [METRIC_FINISHED]: 'Titles marked watched. A season or a year counts only the finishes dated inside it.',
  [METRIC_EPISODES]:
    'Every episode of a title you finished. A title you’re still watching adds your current ' +
    'position, to All time only.',
  [METRIC_REVIEWS]: 'Reviews written.',
  [METRIC_STREAK]:
    'Weeks in a row, Eastern time, with something logged: a finish, a log, a progress update or a review.',
})

export function metricLabel(metric) {
  return METRIC_LABEL[metric] ?? metric
}

export function periodLabel(period) {
  return PERIOD_LABEL[period] ?? period
}

export function metricNote(metric) {
  return METRIC_NOTE[metric] ?? ''
}

/**
 * The value with its unit, per brief §0.2 — but only where a unit is
 * either the design's own (`h` for hours) or correctness-critical (the
 * streak must never read as a bare number that could be mistaken for
 * days, the one thing this whole feature exists to correct). Finished,
 * episodes and reviews render as plain integers — never rounded
 * (`utils/watch_game.py`'s own docstring), and unambiguous under a tab
 * already labelled with the metric's own name.
 */
export function formatBoardValue(metric, value) {
  const amount = typeof value === 'number' && Number.isFinite(value) ? value : 0
  if (metric === METRIC_HOURS) return `${amount} h`
  if (metric === METRIC_STREAK) return `${amount} ${amount === 1 ? 'week' : 'weeks'}`
  return `${amount}`
}

// ═══════════════════════════════════════════════════════════════════════════
// The member board — §0.2 / §2.1/§2.2
// ═══════════════════════════════════════════════════════════════════════════

/** `game.boards[period][metric]`, defensively — the raw, TIE-SHAPED rows
 * (`{rank, value, member_ids}`) `_rank_members` produces. `[]` for
 * anything malformed rather than a thrown error, matching every other
 * reader in this feature (`watchArchive.js::titlesOf`'s own discipline). */
export function boardRows(game, metric, period) {
  const rows = game?.boards?.[period]?.[metric]
  return Array.isArray(rows) ? rows.filter((row) => row && typeof row === 'object') : []
}

/**
 * One row PER MEMBER, tie rank preserved exactly as the derivation gives
 * it — brief §2.2: "Ties share a rank, exactly as `game` gives them.
 * Don't re-rank on the site." A tied `member_ids` array becomes several
 * output rows sharing one `rank`/`value`, in the order the derivation
 * already sorted them (`_rank_members`'s own docstring: "ascending for a
 * deterministic, STATED order") — never re-sorted here by name or
 * anything else.
 */
export function boardMemberRows(game, metric, period) {
  const out = []
  for (const row of boardRows(game, metric, period)) {
    const ids = Array.isArray(row.member_ids) ? row.member_ids : []
    for (const memberId of ids) {
      out.push({ rank: row.rank, value: row.value, memberId })
    }
  }
  return out
}

export function boardMetricTabs(activeMetric) {
  return BOARD_METRICS.map((metric) => ({
    metric,
    label: metricLabel(metric),
    active: metric === activeMetric,
  }))
}

export function boardPeriodTabs(activePeriod) {
  return PERIODS.map((period) => ({
    period,
    label: periodLabel(period),
    active: period === activePeriod,
  }))
}

// ═══════════════════════════════════════════════════════════════════════════
// Season hall of fame — §0.3
// ═══════════════════════════════════════════════════════════════════════════

function capitalize(word) {
  return typeof word === 'string' && word.length > 0 ? word.charAt(0).toUpperCase() + word.slice(1) : ''
}

/** "Summer 2026" from `{year: 2026, season: 'summer'}` — the design's own
 * card heading shape (`HOF[i].season`, HANDOFF §3's invented data),
 * built from the real `{year, season}` pair every hall-of-fame entry
 * carries (`utils/watch_game.py::compute_watch_game`'s own return shape). */
export function seasonLabel(entry) {
  const season = capitalize(entry?.season) || 'Season'
  return entry?.year ? `${season} ${entry.year}` : season
}

/**
 * One row per board, for one hall-of-fame card — brief §0.3: "each row a
 * board and its season leader. The champion is the hours leader ... Mark
 * it on its row, and say how."
 *
 * The Hours row reads `entry.champion`, never `entry.leaders.hours`
 * directly: `champion` IS the hours leader with titles-finished as the
 * tiebreak (spec §4, verbatim), so it is always at least as decisive as
 * the plain hours board — and it can exist in one honest edge `leaders.
 * hours` cannot: `_leader_row` reports no leader at all once the TOP hours
 * value is a meaningless zero, while `_season_champion` can still name a
 * winner by the finished tiebreak alone (`utils/watch_game.py`'s own
 * docstring, "Hall of fame champion/leaders are omitted ... for a metric
 * nobody actually moved" — a season where hours never moved but someone
 * still finished a title with no runtime data is exactly this case).
 * Every OTHER metric has no tiebreak concept at all, so it reads its own
 * `leaders[metric]` directly.
 */
function hallOfFameRow(entry, metric) {
  const label = metricLabel(metric)
  if (metric === METRIC_HOURS) {
    const champion = entry?.champion ?? null
    return {
      metric,
      label,
      hasLeader: champion !== null,
      memberIds: champion ? champion.member_ids ?? [] : [],
      value: champion ? formatBoardValue(metric, champion.hours) : null,
      isChampion: champion !== null,
      championNote: 'Champion · hours, ties broken by titles finished',
    }
  }
  const leader = entry?.leaders?.[metric] ?? null
  return {
    metric,
    label,
    hasLeader: leader !== null,
    memberIds: leader ? leader.member_ids ?? [] : [],
    value: leader ? formatBoardValue(metric, leader.value) : null,
    isChampion: false,
    championNote: null,
  }
}

/**
 * One card per CLOSED season, in the order `game.hall_of_fame.seasons`
 * already carries — newest first (`compute_watch_game`'s own comment:
 * "Newest-closed-season first"), never re-sorted here. An OPEN season has
 * no entry there at all (`_is_season_closed`'s own gate, one module over),
 * so this selector cannot show one even by accident.
 */
export function hallOfFameCards(game) {
  const seasons = game?.hall_of_fame?.seasons
  if (!Array.isArray(seasons)) return []
  return seasons.map((entry) => ({
    key: `${entry?.year}-${entry?.season}`,
    label: seasonLabel(entry),
    rows: BOARD_METRICS.map((metric) => hallOfFameRow(entry, metric)),
  }))
}

// ═══════════════════════════════════════════════════════════════════════════
// The profile's stat tiles — §0.4 / §2.3, the design's `memberVals()`
// (`LB[k].v[id][2]`, the all-time slot), over the real `members[id].
// all_time` row instead.
// ═══════════════════════════════════════════════════════════════════════════

/**
 * The five all-time tiles for one member — `[]` only when `game` itself
 * is not an object (the caller's job to have already gated on `gameOf`
 * before reaching here). A member `game` has never heard of at all (never
 * a watcher, never an adder, on anything) genuinely has no row —
 * `WatchMemberProfile.jsx`'s own existing rule for a profile nobody has
 * touched is to render every section's honest EMPTY state rather than a
 * bespoke "not found," so this returns five real zero-value tiles rather
 * than omitting the section: a member who has done nothing has DONE
 * NOTHING, which is a fact about them, not an absence of one.
 */
export function memberStatTiles(game, memberId) {
  if (!game || typeof game !== 'object') return []
  const row = game.members?.[memberId]?.[PERIOD_ALL_TIME]
  return BOARD_METRICS.map((metric) => ({
    metric,
    label: metricLabel(metric),
    value: formatBoardValue(metric, row ? row[metric] : 0),
  }))
}

// ═══════════════════════════════════════════════════════════════════════════
// Badges — V2-5f, `game.badges` / `game.members[id].badges` (V2-5d,
// `utils/watch_badges.py::compute_watch_badges`). Two places draw them
// (brief §0): the Leaderboards "Badges" section (`badgeCards`, one per
// badge) and the profile's pills (`memberBadges`, one member's own names).
// ═══════════════════════════════════════════════════════════════════════════

/** `game.badges`, or `null` for anything that is not a genuine object —
 * covers both "the key is absent" (an archive published before V2-5d) and
 * a defensively-rejected malformed value. Never throws. The same
 * `gameOf`-shaped guard this file already uses for `game` itself, one
 * level down. */
function badgesOf(game) {
  const badges = game?.badges
  return badges && typeof badges === 'object' && !Array.isArray(badges) ? badges : null
}

/**
 * One card per badge, in `game`'s own order — `Object.keys(game.badges)`,
 * itself `BADGE_ORDER` (`utils/watch_badges.py::compute_watch_badges`'s
 * own docstring: "in the spec's order") — never re-sorted, alphabetised or
 * filtered here. `[]` for a `game` with no `badges` key at all (an archive
 * published before V2-5d merged): brief §2.1, "gives an empty list, so the
 * section isn't drawn. No crash, and no empty box."
 *
 * The name and description are read from `game`, never invented — the
 * prototype's own `BADGES` constant is invented sample data (brief §0:
 * "The copy comes from `game`, never the prototype"). `count` is the
 * member count, and `mine` is a plain equality against `viewerId` — the
 * same shape `BoardRow`'s own `isYou` uses one page over. `viewerId` is
 * `null`-safe: a `null`/`undefined` viewer (nothing in this feature's own
 * architecture reaches this selector signed out — the whole page is
 * gated before it — but the selector itself does not assume that) never
 * matches any real snowflake string, so `mine` reads `false` throughout.
 */
export function badgeCards(game, viewerId) {
  const badges = badgesOf(game)
  if (!badges) return []
  return Object.keys(badges).map((badgeId) => {
    const badge = badges[badgeId] ?? {}
    const memberIds = Array.isArray(badge.member_ids) ? badge.member_ids : []
    return {
      badgeId,
      name: typeof badge.name === 'string' ? badge.name : '',
      description: typeof badge.description === 'string' ? badge.description : '',
      count: memberIds.length,
      mine: viewerId != null && memberIds.includes(viewerId),
    }
  })
}

/**
 * One member's own badge NAMES, in `game`'s order — brief §2.1's second
 * selector. Reads `game.members[id].badges` (a list of badge ids, V2-5d,
 * already in `BADGE_ORDER` — `compute_watch_game`'s own comment: "in the
 * spec's order" — never re-sorted here either) and resolves each id
 * against `game.badges` for its display name.
 *
 * `[]` for a `game` with no `badges` key, a member `game` has never heard
 * of, or a member with a genuinely empty list — one contract for all
 * three, matching `badgeCards`' own "no crash, no empty box" above,
 * applied to the profile's pills: brief §0, "No pills and no box when the
 * member has none."
 */
export function memberBadges(game, memberId) {
  const badges = badgesOf(game)
  if (!badges) return []
  const ids = game?.members?.[memberId]?.badges
  if (!Array.isArray(ids)) return []
  return ids.map((badgeId) => badges[badgeId]?.name).filter((name) => typeof name === 'string' && name.length > 0)
}

// ═══════════════════════════════════════════════════════════════════════════
// Content boards — V2-5g, `game.content_boards` (V2-5e, `utils/watch_
// content_boards.py::compute_content_boards`). Six boards, the spec's own
// order, top rated / most watched / hidden gems / crew classics / most
// contrarian / best episodes — the design's "What the group rates" section.
//
// 🔴 GOTCHA 21, THE WHOLE POINT OF THIS SECTION: the bot has already
// filtered, ranked, tie-broken and capped every one of these at 10 rows
// (`BOARD_ROW_CAP`). Nothing below re-derives a single one of those
// decisions — it reads the row the bot already built, formats ITS OWN stat
// text from the fields that row carries, and caps a SECOND time at 6 (the
// design's own row count) purely as a DISPLAY limit, never a re-ranking.
// ═══════════════════════════════════════════════════════════════════════════

export const BOARD_TOP_RATED = 'top_rated'
export const BOARD_MOST_WATCHED = 'most_watched'
export const BOARD_HIDDEN_GEMS = 'hidden_gems'
export const BOARD_CREW_CLASSICS = 'crew_classics'
export const BOARD_MOST_CONTRARIAN = 'most_contrarian'
export const BOARD_BEST_EPISODES = 'best_episodes'

/** `utils/watch_content_boards.py::BOARD_ORDER`, verbatim — the spec's own
 * order (§4). Duplicated rather than imported across the Python/JS
 * boundary, the identical convention CLAUDE.md gotcha 21 already states for
 * `crew_average_rating`/`crewAverageRating`: two languages, one contract,
 * pinned against each other by the shared fixture rather than assumed to
 * agree (see the "shared fixture" describe block in this file's own test
 * suite, and `tests/test_watch_content_boards.py::
 * TestFixtureCarriesContentBoards`, the bot-side half of the same pin). */
export const CONTENT_BOARDS = Object.freeze([
  BOARD_TOP_RATED,
  BOARD_MOST_WATCHED,
  BOARD_HIDDEN_GEMS,
  BOARD_CREW_CLASSICS,
  BOARD_MOST_CONTRARIAN,
  BOARD_BEST_EPISODES,
])

/** Brief §2: the design's own default tab (`D2D Watch v2.dc.html`'s
 * `cbBoard: 'top'`). */
export const DEFAULT_CONTENT_BOARD = BOARD_TOP_RATED

/** The design's own row count (`boards()`'s `cb: cbb.list.slice(0, 6)`) —
 * a SITE-side display cap, layered on top of the bot's own 10-row cap
 * (`BOARD_ROW_CAP`), never a replacement for it. */
const CONTENT_BOARD_ROW_CAP = 6

// Pinned against cogs/watch/top.py — tests/test_watch_top.py::TestWordingMatchesTheSite keeps these words identical on both surfaces.
const CONTENT_BOARD_LABEL = Object.freeze({
  [BOARD_TOP_RATED]: 'Top rated',
  [BOARD_MOST_WATCHED]: 'Most watched',
  [BOARD_HIDDEN_GEMS]: 'Hidden gems',
  [BOARD_CREW_CLASSICS]: 'Crew classics',
  [BOARD_MOST_CONTRARIAN]: 'Most contrarian',
  [BOARD_BEST_EPISODES]: 'Best episodes',
})

/**
 * One line under the tabs, said from the SPEC's own rule
 * (`utils/watch_content_boards.py`'s own module docstring and its per-board
 * constants) — never the design prototype's own REJECTED definitions.
 * Brief §0, verbatim: the prototype's hidden gems reads "Rated 8.5 or more,"
 * and its "Crew classics" note ("What the most people finished") is
 * actually the spec's definition of a DIFFERENT board, *Most watched* — the
 * spec's own Crew classics is "every finisher rated it 8+," a stricter,
 * different rule. Both would be quietly wrong copy if carried over here.
 */
// Pinned against cogs/watch/top.py — tests/test_watch_top.py::TestWordingMatchesTheSite keeps these words identical on both surfaces.
const CONTENT_BOARD_NOTE = Object.freeze({
  [BOARD_TOP_RATED]: 'Crew average, three ratings or more.',
  [BOARD_MOST_WATCHED]: 'Members who finished it, most first.',
  [BOARD_HIDDEN_GEMS]: 'Crew average 8 or better, seen by two members or fewer.',
  [BOARD_CREW_CLASSICS]: 'Every finisher rated it 8 or better, three finishers or more.',
  [BOARD_MOST_CONTRARIAN]: "Widest gap between the crew's average and the world's score, three ratings or more.",
  [BOARD_BEST_EPISODES]: "Most reactions on an episode's thread, across the whole catalog.",
})

/**
 * What an EMPTY board says instead of an empty list — brief §2: "an empty
 * board says so in one plain line... Never an empty list." Each board
 * restates its OWN qualifying rule rather than one generic sentence, so a
 * member reads why nothing is here yet, not just that nothing is.
 */
// Pinned against cogs/watch/top.py — tests/test_watch_top.py::TestWordingMatchesTheSite keeps these words identical on both surfaces.
const CONTENT_BOARD_EMPTY_TEXT = Object.freeze({
  [BOARD_TOP_RATED]: 'Nothing qualifies yet: three ratings or more.',
  [BOARD_MOST_WATCHED]: 'Nobody has finished anything yet.',
  [BOARD_HIDDEN_GEMS]: 'Nothing qualifies yet: crew average 8 or better, seen by two or fewer.',
  [BOARD_CREW_CLASSICS]: 'Nothing qualifies yet: three finishers or more, all rating it 8 or better.',
  [BOARD_MOST_CONTRARIAN]: 'Nothing qualifies yet: three ratings or more.',
  [BOARD_BEST_EPISODES]: 'No episode has a reaction yet.',
})

export function contentBoardLabel(boardId) {
  return CONTENT_BOARD_LABEL[boardId] ?? boardId
}

export function contentBoardNote(boardId) {
  return CONTENT_BOARD_NOTE[boardId] ?? ''
}

export function contentBoardEmptyText(boardId) {
  return CONTENT_BOARD_EMPTY_TEXT[boardId] ?? ''
}

export function contentBoardTabs(activeBoard) {
  return CONTENT_BOARDS.map((boardId) => ({
    boardId,
    label: contentBoardLabel(boardId),
    active: boardId === activeBoard,
  }))
}

/** `game.content_boards`, or `null` for anything that is not a genuine
 * object — the same `gameOf`/`badgesOf`-shaped guard one level down, and
 * the one place this file tells "not published yet" (an archive from
 * before V2-5e) apart from "published, and this specific board is
 * genuinely empty." Never throws. */
function contentBoardsOf(game) {
  const boards = game?.content_boards
  return boards && typeof boards === 'object' && !Array.isArray(boards) ? boards : null
}

/**
 * Whether `game` carries the content-boards key AT ALL. Brief §2's "no
 * empty box" rule is about a board with no QUALIFYING row, which is a
 * different, real state from the whole FEATURE not existing on an archive
 * published before V2-5e — the same distinction `gameOf` already draws for
 * `game` itself, one level up. Unlike `badgeCards` (whose `length > 0`
 * doubles as this check, because every one of its 8 badges always exists
 * once published, even at a zero count), a content board's row list can be
 * genuinely empty AFTER publishing too — nobody may have posted three
 * ratings on anything yet — so THIS file cannot infer "published" from "has
 * rows" the way the badges selector does. `WatchLeaderboards.jsx` reads
 * this once, to decide whether to draw the section at all, never per board.
 */
export function contentBoardsPublished(game) {
  return contentBoardsOf(game) !== null
}

function rawContentBoardRows(game, boardId) {
  const boards = contentBoardsOf(game)
  const rows = boards?.[boardId]
  return Array.isArray(rows) ? rows.filter((row) => row && typeof row === 'object') : []
}

/** One decimal place, matching every worked example in the brief ("9.3
 * avg", "9.0 avg", "crew 9.0 · the world 7.5") — `null` for anything that
 * is not a finite number, so a malformed row degrades to an omitted clause
 * rather than "NaN avg" or a thrown error. */
function average1(value) {
  return typeof value === 'number' && Number.isFinite(value) ? value.toFixed(1) : null
}

/** Brief §2: "Top rated: `9.3 avg`." Every published row already cleared
 * the 3-counted-ratings gate and has a real average (`utils/watch_content_
 * boards.py::_top_rated_rows` never publishes one without both) — the
 * `null` branch is this file's own defensive floor, never reachable from
 * real data, matching every other selector's discipline here. */
function topRatedStat(row) {
  const avg = average1(row.average)
  return avg === null ? '' : `${avg} avg`
}

/** Brief §2: "Most watched: `3 finished · 8.7 avg`, leaving out the average
 * when it's null." Unlike top rated, a real published row here CAN carry a
 * `null` average (finishing needs no rating at all) — this is the one stat
 * builder where that branch is a genuine, expected case, not a defensive
 * floor. */
function mostWatchedStat(row) {
  const count = typeof row.finished_count === 'number' ? row.finished_count : 0
  const avg = average1(row.average)
  return avg === null ? `${count} finished` : `${count} finished · ${avg} avg`
}

/** Brief §2: "Crew classics: `4 finished · 9.3 avg`." */
function crewClassicsStat(row) {
  const count = typeof row.finisher_count === 'number' ? row.finisher_count : 0
  const avg = average1(row.average)
  return avg === null ? `${count} finished` : `${count} finished · ${avg} avg`
}

/**
 * Brief §2: "Most contrarian: `crew 9.0 · {provider} 7.5`. Say how you name
 * the provider's side; the row may not carry which provider it is. If it
 * doesn't, write `the world 7.5` rather than guess." It doesn't:
 * `_most_contrarian_rows` publishes `crew_average`/`provider_score`/`gap`,
 * never which provider supplied the score (`utils/watch_content_boards.py`
 * §2's own row shape) — so "the world" is what this file says, always,
 * rather than guessing AniList or TMDB from a field that isn't there.
 */
function mostContrarianStat(row) {
  const crew = average1(row.crew_average)
  const world = average1(row.provider_score)
  if (crew === null || world === null) return ''
  return `crew ${crew} · the world ${world}`
}

/** Best episodes: `{episode label} · N reactions`, singular when N is 1.
 * Manager review: the brief's format also restated the title, but every row
 * already renders its own title as its heading (`ContentBoardRow`), so two
 * episodes of one show are still told apart — by the label, under the same
 * heading — and the stat line no longer repeats what sits right above it. */
function bestEpisodesStat(row) {
  const label = typeof row.episode_label === 'string' ? row.episode_label : ''
  const count = typeof row.count === 'number' ? row.count : 0
  return `${label} · ${count} ${count === 1 ? 'reaction' : 'reactions'}`
}

/** Brief §2: "Hidden gems: `9.0 avg · only {names}`, with names from the
 * member directory, as elsewhere on the page." Names are NOT this file's
 * job — `<People>` needs `directory` and JSX, which a pure selector should
 * not reach for (the identical split `watchArchive.js::summaryLineText`'s
 * own header already draws, for the same reason: "Member NAMES are the
 * caller's own job... a pure helper should not reach for" JSX). This
 * returns only the plain-text PREFIX; `contentBoardRows` below pairs it
 * with the row's own `member_ids`, and `WatchLeaderboards.jsx` renders the
 * names. */
function hiddenGemsPrefix(row) {
  const avg = average1(row.average)
  return avg === null ? '' : `${avg} avg · only `
}

/**
 * One board's stat text (or, for hidden gems, its plain-text PREFIX) plus
 * the member ids the caller should append as names — one shape for every
 * board, so `WatchLeaderboards.jsx` never needs a per-board branch beyond
 * "is `memberIds` non-empty."
 */
const STAT_BUILDERS = Object.freeze({
  [BOARD_TOP_RATED]: (row) => ({ stat: topRatedStat(row), memberIds: [] }),
  [BOARD_MOST_WATCHED]: (row) => ({ stat: mostWatchedStat(row), memberIds: [] }),
  [BOARD_HIDDEN_GEMS]: (row) => ({
    stat: hiddenGemsPrefix(row),
    memberIds: Array.isArray(row.member_ids) ? row.member_ids : [],
  }),
  [BOARD_CREW_CLASSICS]: (row) => ({ stat: crewClassicsStat(row), memberIds: [] }),
  [BOARD_MOST_CONTRARIAN]: (row) => ({ stat: mostContrarianStat(row), memberIds: [] }),
  [BOARD_BEST_EPISODES]: (row) => ({ stat: bestEpisodesStat(row), memberIds: [] }),
})

/**
 * Up to `CONTENT_BOARD_ROW_CAP` ranked rows for one board — `rank`,
 * `subscriptionId` (every row links to `/watch/<id>`, brief §2), the plain
 * `title`, this board's own stat text, and `memberIds` (non-empty ONLY for
 * hidden gems). `[]` for an unknown `boardId` or anything malformed,
 * matching every other selector's "no crash, no empty box" discipline in
 * this file.
 *
 * NEVER re-filters, re-sorts, or re-caps beyond taking the first six — the
 * bot has already ranked, tie-broken and capped at 10 (`BOARD_ROW_CAP`);
 * this only reads that order (gotcha 21).
 */
export function contentBoardRows(game, boardId) {
  const build = STAT_BUILDERS[boardId]
  if (!build) return []
  return rawContentBoardRows(game, boardId)
    .slice(0, CONTENT_BOARD_ROW_CAP)
    .map((row) => {
      const { stat, memberIds } = build(row)
      return {
        rank: typeof row.rank === 'number' ? row.rank : null,
        subscriptionId: row.subscription_id != null ? String(row.subscription_id) : null,
        title: typeof row.title === 'string' ? row.title : '',
        stat,
        memberIds,
      }
    })
}
